import { Stack, SxProps, Theme, Typography, useTheme } from "@mui/material";
import { Button } from "components/button";
import { InfoIcon } from "components/icons";
import React from "react";

type BannerVariant = "warning" | "success" | "error" | "info";

interface VariantStyles {
  backgroundColor: string;
  borderColor: string;
  iconColor: string;
  /**
   * Only where the design specifies one. Left out, the heading keeps the
   * theme's text colour — the icon's colour is not a safe stand-in, because
   * the tones that read well as a 24px glyph do not all clear the contrast
   * floor as 14px text.
   */
  titleColor?: string;
}

interface ConfirmationBannerProps {
  onConfirm?: () => void;
  onCancel?: () => void;
  description: React.ReactNode;
  /**
   * Optional headline above the description. Present, it also switches the
   * body to regular weight — a semibold line under a semibold line reads as
   * one block, which is the whole point of having a heading.
   */
  title?: React.ReactNode;
  confirmButtonLabel?: string;
  cancelButtonLabel?: string;
  isLoading?: boolean;
  containerStyle?: SxProps<Theme>;
  variant?: BannerVariant;
  variantStyles?: Partial<Record<BannerVariant, Partial<VariantStyles>>>;
}

const getVariantColors = (
  theme: Theme,
): Record<BannerVariant, VariantStyles> => {
  const { yellow, green, red, blue } = theme.palette.primitive.system;
  return {
    warning: {
      backgroundColor: yellow[25] ?? "#FFF4CB",
      borderColor: yellow[800] ?? "#F3C423",
      iconColor: yellow[800] ?? "#F3C423",
    },
    success: {
      backgroundColor: green[100] ?? "#d8f1db",
      borderColor: green[800] ?? "#00722A",
      iconColor: green[800] ?? "#00722A",
    },
    error: {
      backgroundColor: red[100] ?? "#ffdbd9",
      borderColor: red[800] ?? "#ce293a",
      iconColor: red[800] ?? "#ce293a",
    },
    info: {
      backgroundColor: blue[25] ?? "#f5f9fe",
      borderColor: blue[400] ?? "#98c6f1",
      iconColor: blue[800] ?? "#0c5d97",
      titleColor: blue[800] ?? "#0c5d97",
    },
  };
};

export const ConfirmationBanner = ({
  onConfirm,
  onCancel,
  description,
  title,
  confirmButtonLabel = "Sure",
  cancelButtonLabel = "Cancel",
  isLoading = false,
  containerStyle,
  variant = "warning",
  variantStyles,
}: ConfirmationBannerProps) => {
  const theme = useTheme();
  const defaultColors = getVariantColors(theme);
  const currentVariantStyles = {
    ...defaultColors[variant],
    ...variantStyles?.[variant],
  };

  const showActions = onConfirm || onCancel;

  return (
    <Stack
      direction="row"
      sx={{
        p: "8px 16px",
        backgroundColor: currentVariantStyles.backgroundColor,
        borderRadius: "4px",
        border: `1px solid ${currentVariantStyles.borderColor}`,
        justifyContent: "space-between",
        alignItems: "center",
        ...containerStyle,
      }}
    >
      <Stack
        direction="row"
        sx={{
          // Centred against a single line, but against the FIRST line once
          // there is a heading — otherwise the icon drifts to the middle of
          // the block and stops reading as a marker for the headline.
          alignItems: title ? "flex-start" : "center",
          gap: "8px",
        }}
      >
        <InfoIcon htmlColor={currentVariantStyles.iconColor} />
        {title ? (
          <Stack gap="8px">
            <Typography
              variant="subtitle1"
              color={currentVariantStyles.titleColor}
            >
              {title}
            </Typography>
            <Typography component="span" variant="body-small">
              {description}
            </Typography>
          </Stack>
        ) : (
          <Typography variant="button-label">{description}</Typography>
        )}
      </Stack>
      {showActions && (
        <Stack direction="row" gap="16px">
          {onConfirm && (
            <Button
              disabled={isLoading}
              variant="ghost"
              isLink
              onClick={onConfirm}
            >
              {confirmButtonLabel}
            </Button>
          )}
          {onCancel && (
            <Button
              disabled={isLoading}
              variant="ghost"
              isLink
              onClick={onCancel}
            >
              {cancelButtonLabel}
            </Button>
          )}
        </Stack>
      )}
    </Stack>
  );
};
